import { DefaultCustomField } from 'src/constants/types';
import { formatCustomFieldToHtml as formatCustomFieldHelper } from 'src/helpers/formatCustomField';

const gsmCharset: Set<string> = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1BÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
);

// Characters that cannot be recovered via NFD decomposition but have a
// close GSM-7 equivalent. Keep this list small — NFD handles the rest.
const unicodeToGsmMap: Record<string, string> = {
  // Smart/curly quotes -> straight equivalents
  '“': '"',  // " left double quotation mark
  '”': '"',  // " right double quotation mark
  '‘': "'",  // ' left single quotation mark
  '’': "'",  // ' right single quotation mark
  '‚': "'",  // ‚ single low-9 quotation mark
  '„': '"',  // „ double low-9 quotation mark
  'ʼ': "'",  // ʼ modifier letter apostrophe (common in iOS copy-paste)
  '´': "'",  // ´ acute accent (sometimes used as apostrophe)
  '′': "'",  // ′ prime
  '″': '"',  // ″ double prime

  // Dashes -> hyphen-minus
  '–': '-',  // – en dash
  '—': '-',  // — em dash
  '‐': '-',  // ‐ hyphen
  '‑': '-',  // ‑ non-breaking hyphen
  '‒': '-',  // ‒ figure dash
  '−': '-',  // − minus sign

  // Other punctuation
  '…': '...', // … horizontal ellipsis
  '•': '*',   // • bullet
  '·': '.',   // · middle dot

  // Accented chars with no NFD decomposition -> nearest ASCII equivalent
  'Ł': 'L',  // Ł L with stroke
  'ł': 'l',  // ł l with stroke
  'Đ': 'D',  // Đ D with stroke
  'đ': 'd',  // đ d with stroke
  'Ð': 'D',  // Ð eth uppercase
  'ð': 'd',  // ð eth lowercase
  'Þ': 'T',  // Þ thorn uppercase
  'þ': 't',  // þ thorn lowercase
  'Ŋ': 'N',  // Ŋ eng uppercase
  'ŋ': 'n',  // ŋ eng lowercase
  'ı': 'i',  // ı dotless i (Turkish)

  // Invisible/zero-width characters -> strip
  '​': '',   // zero-width space
  '﻿': '',   // BOM / zero-width no-break space
};

// U+00A0 no-break space, U+202F narrow no-break space, U+2007 figure space, U+2009 thin space
const nonBreakingSpacePattern = /(?:[\u00A0\u202F\u2007\u2009]|&nbsp;|&#160;|&#xA0;)/gi;

// Combining diacritical marks range (U+0300–U+036F)
const combiningMarksPattern = /[̀-ͯ]/g;

export const convertToGsm = (input: string): string => {
  // NFC normalization ensures precomposed characters (e.g. ñ from NFD clipboard)
  // match the gsmCharset entries and are not accidentally stripped by the NFD path.
  const normalizedInput = input.normalize('NFC').replaceAll(nonBreakingSpacePattern, ' ');
  let result = '';
  for (const char of normalizedInput) {
    if (gsmCharset.has(char)) {
      result += char;
    } else if (char in unicodeToGsmMap) {
      result += unicodeToGsmMap[char];
    } else {
      // Strip combining diacritical marks (covers most accented Latin chars).
      // Only substitute when every resulting character is already in GSM-7,
      // so we never silently mangle characters that genuinely need Unicode.
      const stripped = char.normalize('NFD').replace(combiningMarksPattern, '');
      if (stripped.length > 0 && stripped !== char && [...stripped].every(c => gsmCharset.has(c))) {
        result += stripped;
      } else {
        result += char;
      }
    }
  }
  return result;
};

export const formatMessage = (message: string, customFields: DefaultCustomField[]) => {
  const gsmMessage = convertToGsm(message);
  const formattedMessage = gsmMessage
    .replaceAll('\n', '<br/>')
    .replaceAll(/\{\{([^{}]+)\}\}/g, (match: string) => {
      return formatCustomFieldHelper(match, customFields, { includeClass: true });
    });
  return formatUnicodeStylesToHtml(formattedMessage);
};

type TextStyle = {
  bold: boolean;
  italic: boolean;
};

const uppercaseStart = 'A'.charCodeAt(0);
const lowercaseStart = 'a'.charCodeAt(0);
const digitStart = '0'.charCodeAt(0);

const boldUppercaseStart = 0x1d400;
const boldLowercaseStart = 0x1d41a;
const boldDigitStart = 0x1d7ce;
const italicUppercaseStart = 0x1d434;
const italicLowercaseStart = 0x1d44e;
const boldItalicUppercaseStart = 0x1d468;
const boldItalicLowercaseStart = 0x1d482;

const italicSpecialChars: Record<string, string> = {
  h: 'ℎ',
};

const reverseUnicodeStyleMap = new Map<string, { value: string; style: TextStyle }>();

const addReverseRange = (
  sourceStart: number,
  styledStart: number,
  length: number,
  style: TextStyle,
) => {
  for (let index = 0; index < length; index += 1) {
    reverseUnicodeStyleMap.set(String.fromCodePoint(styledStart + index), {
      value: String.fromCharCode(sourceStart + index),
      style,
    });
  }
};

addReverseRange(uppercaseStart, boldUppercaseStart, 26, { bold: true, italic: false });
addReverseRange(lowercaseStart, boldLowercaseStart, 26, { bold: true, italic: false });
addReverseRange(digitStart, boldDigitStart, 10, { bold: true, italic: false });
addReverseRange(uppercaseStart, italicUppercaseStart, 26, { bold: false, italic: true });
addReverseRange(lowercaseStart, italicLowercaseStart, 26, { bold: false, italic: true });
addReverseRange(uppercaseStart, boldItalicUppercaseStart, 26, { bold: true, italic: true });
addReverseRange(lowercaseStart, boldItalicLowercaseStart, 26, { bold: true, italic: true });
reverseUnicodeStyleMap.set(italicSpecialChars.h, {
  value: 'h',
  style: { bold: false, italic: true },
});

const escapeHtml = (value: string): string => {
  const container = document.createElement('div');
  container.textContent = value;
  return container.innerHTML;
};

const styleChar = (char: string, style: TextStyle): string => {
  const codePoint = char.codePointAt(0);

  if (!codePoint || (!style.bold && !style.italic)) {
    return char;
  }

  if (codePoint >= uppercaseStart && codePoint <= uppercaseStart + 25) {
    if (style.bold && style.italic) {
      return String.fromCodePoint(boldItalicUppercaseStart + codePoint - uppercaseStart);
    }
    if (style.bold) {
      return String.fromCodePoint(boldUppercaseStart + codePoint - uppercaseStart);
    }
    if (style.italic) {
      return String.fromCodePoint(italicUppercaseStart + codePoint - uppercaseStart);
    }
  }

  if (codePoint >= lowercaseStart && codePoint <= lowercaseStart + 25) {
    if (style.bold && style.italic) {
      return String.fromCodePoint(boldItalicLowercaseStart + codePoint - lowercaseStart);
    }
    if (style.bold) {
      return String.fromCodePoint(boldLowercaseStart + codePoint - lowercaseStart);
    }
    if (style.italic) {
      return italicSpecialChars[char] || String.fromCodePoint(italicLowercaseStart + codePoint - lowercaseStart);
    }
  }

  if (style.bold && codePoint >= digitStart && codePoint <= digitStart + 9) {
    return String.fromCodePoint(boldDigitStart + codePoint - digitStart);
  }

  return char;
};

const applyUnicodeStyle = (value: string, style: TextStyle): string => (
  [...value].map(char => styleChar(char, style)).join('')
);

const getUnicodeCharStyle = (char: string): { value: string; style: TextStyle } => (
  reverseUnicodeStyleMap.get(char) || { value: char, style: { bold: false, italic: false } }
);

export const formatEditorHtmlToMessageText = (html: string | null, placeholder = ''): string => {
  const container = document.createElement('div');
  container.innerHTML = html || '';

  const readNode = (node: Node, style: TextStyle): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return applyUnicodeStyle(node.textContent || '', style);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'br') {
      return '\n';
    }

    if (
      element.classList.contains('mention') &&
      element.dataset.id &&
      !element.classList.contains('mention-link')
    ) {
      return `{{${element.dataset.id}}}`;
    }

    const nextStyle = {
      bold: style.bold || tagName === 'strong' || tagName === 'b',
      italic: style.italic || tagName === 'em' || tagName === 'i',
    };

    const value = Array.from(element.childNodes)
      .map(child => readNode(child, nextStyle))
      .join('');

    return ['p', 'div'].includes(tagName) ? `${value}\n` : value;
  };

  const result = Array.from(container.childNodes)
    .map(child => readNode(child, { bold: false, italic: false }))
    .join('')
    .replace(/[^\S\n\r]*\n$/, '');

  return result || placeholder;
};

export const formatUnicodeStylesToHtml = (html: string): string => {
  const container = document.createElement('div');
  container.innerHTML = html;

  const formatText = (value: string): string => {
    let result = '';
    let currentValue = '';
    let currentStyle: TextStyle | null = null;

    const flush = () => {
      if (!currentValue || !currentStyle) {
        return;
      }

      const escapedValue = escapeHtml(currentValue);
      if (currentStyle.bold && currentStyle.italic) {
        result += `<strong><em>${escapedValue}</em></strong>`;
      } else if (currentStyle.bold) {
        result += `<strong>${escapedValue}</strong>`;
      } else if (currentStyle.italic) {
        result += `<em>${escapedValue}</em>`;
      } else {
        result += escapedValue;
      }
      currentValue = '';
    };

    [...value].forEach(char => {
      const formattedChar = getUnicodeCharStyle(char);
      const nextStyle = formattedChar.style;

      if (
        !currentStyle ||
        currentStyle.bold !== nextStyle.bold ||
        currentStyle.italic !== nextStyle.italic
      ) {
        flush();
        currentStyle = nextStyle;
      }

      currentValue += formattedChar.value;
    });

    flush();
    return result;
  };

  const formatNode = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      const replacement = document.createElement('span');
      replacement.innerHTML = formatText(node.textContent);
      const parentNode = node.parentNode;
      if (!parentNode) {
        return;
      }
      Array.from(replacement.childNodes).forEach(child => {
        parentNode.insertBefore(child, node);
      });
      parentNode.removeChild(node);
      return;
    }

    Array.from(node.childNodes).forEach(formatNode);
  };

  Array.from(container.childNodes).forEach(formatNode);

  return container.innerHTML;
};

export const formatUnicodeStylesToSafeHtml = (text: string | null | undefined): string => {
  const container = document.createElement('div');
  container.textContent = text || '';
  return formatUnicodeStylesToHtml(container.innerHTML);
};
